import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import Box from '@tds/core-box';
import Button from '@tds/core-button';
import Spinner from '@tds/core-spinner';
import DimpleDivider from '@tds/core-dimple-divider';
import Heading from '@tds/core-heading';
import Notification from '@tds/core-notification';
import { Download } from '@tds/core-interactive-icon';

import request from '../../utils/request';
import { ErrorMessageWrapper } from '../css';

const BrpFileUpload = ({ data }) => {
  const { name, required, label, onChange, formErrors, value } = data;
  const [selectedFiles, setSelectedFiles] = useState();
  const [showError, setShowError] = useState({ isVisible: false, message: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const inputRef = useRef();
  const fieldError = _.get(formErrors, `${name}.text`, false);
  let confirmUpload = false;

  const onFileSelect = e => {
    const {
      target: { files }
    } = e;

    setShowError({ isVisible: false, message: '' });
    setSelectedFiles(files);
  };

  const uploadFile = () => {
    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i += 1) {
      formData.append('files', selectedFiles[i]);
    }
    return request.post(`/brp/fileupload`, formData);
  };

  const onFileUpload = async () => {
    if (value && !confirmUpload) {
      setShowWarning(true);
      return;
    }
    if (value && confirmUpload) setShowWarning(false);

    if (!selectedFiles) {
      setShowError({
        isVisible: true,
        message: 'Please select the file'
      });
      return;
    }
    setIsUploading(true);
    try {
      const result = await uploadFile();
      if (result) {
        onChange({
          target: {
            name,
            value: _.get(result, 'data.data', []),
            checked: false
          }
        });
        setIsUploading(false);
        setSelectedFiles(null);
        inputRef.current.value = '';
      }
    } catch (error) {
      setShowError({
        isVisible: true,
        message: 'There was an error uploading the file'
      });
      setIsUploading(false);
      throw error;
    }
  };

  const onFileDownload = url => {
    window.open(url, '_blank');
  };

  const confirmFileUpload = () => {
    confirmUpload = true;
    onFileUpload();
  };

  return (
    <Box between={2}>
      <div>
        <span className="frg-body-font-14px">
          {label}
          {required && `*`}
        </span>
      </div>
      <Box>
        <input
          type="file"
          name={name}
          onChange={onFileSelect}
          accept="application/msword, application/pdf, image/jpeg, image/png"
          ref={inputRef}
          multiple
        />
      </Box>
      <Box>
        {!showWarning && (
          <Spinner
            label="Uploading file..."
            size="small"
            spinning={isUploading}
            inline
          >
            <Button onClick={onFileUpload}>Upload</Button>
          </Spinner>
        )}
        {showWarning && (
          <Box between="2">
            <Notification variant="warning" copy="en">
              Warning: Previous attachments will be removed, do you want to
              continue?
            </Notification>
            <Box
              between={{ xs: 1, md: 1, xl: 2 }}
              inline={{ xs: false, lg: true }}
            >
              <Button onClick={confirmFileUpload}>Yes</Button>
              <Button variant="secondary" onClick={() => setShowWarning(false)}>
                No
              </Button>
            </Box>
          </Box>
        )}

        {showError.isVisible && (
          <ErrorMessageWrapper>{showError.message}</ErrorMessageWrapper>
        )}
        {!showError.isVisible && !value && fieldError && (
          <ErrorMessageWrapper>{fieldError}</ErrorMessageWrapper>
        )}
      </Box>
      {value && (
        <>
          <DimpleDivider />
          <Box between="3">
            <Heading level="h4">Uploaded attachments</Heading>
            <ul className="list-group list-group-flush">
              {value &&
                value.map(imgaeUrl => (
                  <li>
                    <div
                      className="card"
                      style={{
                        margin: '0.5rem 0',
                        display: 'flex',
                        flexDirection: 'row',
                        padding: '1rem 3rem',
                        justifyContent: 'space-between'
                      }}
                    >
                      <p>{imgaeUrl}</p>
                      <Download
                        onClick={() => onFileDownload(imgaeUrl)}
                        a11yText="Download"
                      />
                    </div>
                  </li>
                ))}
            </ul>
          </Box>
        </>
      )}
    </Box>
  );
};

BrpFileUpload.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    formErrors: PropTypes.objectOf(PropTypes.any).isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired
};

export default BrpFileUpload;
